import { ApiResp, ApiRespSchema } from "@garther/interfaces";
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { z } from "zod";

import { firebaseService } from "../firebase";

export interface ApiClientConfig {
  baseURL: string;
  baseEndpoint?: string;
  timeout?: number;
  headers?: Record<string, string>;
  requireAuth?: boolean; // Whether this client requires authentication
}

export interface RequestOptions extends AxiosRequestConfig {
  requireAuth?: boolean; // Override for individual requests
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: any
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class BaseApiClient {
  private _config: ApiClientConfig;
  private _baseEndpoint: string;
  private _axiosInstance: AxiosInstance;

  constructor(config: ApiClientConfig) {
    this._config = {
      timeout: 10000, // 10 seconds default
      headers: {
        "Content-Type": "application/json"
      },
      requireAuth: false,
      ...config
    };
    this._baseEndpoint = config.baseEndpoint?.replace(/^\//, "").replace(/\/$/, "") || "";

    // Create axios instance
    this._axiosInstance = axios.create({
      baseURL: this._config.baseURL,
      timeout: this._config.timeout,
      headers: this._config.headers
    });

    // Add request interceptor for authentication
    this._axiosInstance.interceptors.request.use(
      async (config) => {
        const requireAuth = (config as any).requireAuth ?? this._config.requireAuth;

        try {
          const currentUser = firebaseService.currentUser;
          if (!currentUser) {
            throw new ApiError("Authentication required but no valid user found", 401);
          }
          const token = await currentUser.getIdToken();
          if (!token) {
            throw new ApiError("Authentication required but no valid token found", 401);
          }
          config.headers.Authorization = `Bearer ${token}`;
        } catch (error) {
          console.error("Error getting authentication token", error);
          if (requireAuth) {
            if (error instanceof ApiError) {
              throw error;
            }
            throw new ApiError("Failed to get authentication token", 401);
          } else {
            return config;
          }
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this._axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error: AxiosError) => {
        if (error.response) {
          // Server responded with error status
          const status = error.response.status;
          const data = error.response.data as any;
          const message = data?.message || error.message || `HTTP error! status: ${status}`;

          throw new ApiError(message, status, data);
        } else if (error.request) {
          // Request was made but no response received (network error)
          throw new ApiError("Network error - no response received", 0);
        } else {
          // Something else happened
          throw new ApiError(error.message || "Unknown error occurred", 0);
        }
      }
    );
  }

  private endpoint(extend: string): string {
    return `/${this._baseEndpoint}/${extend.replace(/^\//, "")}`;
  }

  private async makeRequest(
    endpoint: string,
    schema: z.Schema,
    options: RequestOptions = {}
  ): Promise<z.infer<typeof schema>> {
    const url = this.endpoint(endpoint);

    try {
      const resp = await this._axiosInstance.request<ApiResp<z.infer<typeof schema>>>({
        url,
        ...options
      });

      const respParsed = ApiRespSchema(schema).parse(resp.data);
      if (!respParsed.success || !respParsed.data) {
        throw new ApiError(respParsed.error || "API request failed", respParsed.status || 400, respParsed);
      }

      return respParsed.data;
    } catch (error) {
      // Error is already handled by the response interceptor
      console.error("Error making request", error);
      throw error;
    }
  }

  // // Generic method for API responses that follow our standard format
  // protected async apiRequest<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  //   const response: ApiResp<T> = await this.makeRequest(endpoint, options);

  //   if (!response.success) {
  //     throw new ApiError(response.error || "API request failed", response.status || 400, response);
  //   }

  //   return response.data as T;
  // }

  // GET request
  protected async get(
    endpoint: string,
    schema: z.Schema,
    options: RequestOptions = {}
  ): Promise<z.infer<typeof schema>> {
    return this.makeRequest(endpoint, schema, {
      method: "GET",
      ...options
    });
  }

  // POST request
  protected async post(
    endpoint: string,
    schema: z.Schema,
    data: any,
    options: RequestOptions = {}
  ): Promise<z.infer<typeof schema>> {
    return this.makeRequest(endpoint, schema, {
      method: "POST",
      data,
      ...options
    });
  }

  // PUT request
  protected async put(
    endpoint: string,
    schema: z.Schema,
    data?: any,
    options: RequestOptions = {}
  ): Promise<z.infer<typeof schema>> {
    return this.makeRequest(endpoint, schema, {
      method: "PUT",
      data,
      ...options
    });
  }

  // DELETE request
  protected async delete(
    endpoint: string,
    schema: z.Schema,
    options: RequestOptions = {}
  ): Promise<z.infer<typeof schema>> {
    return this.makeRequest(endpoint, schema, {
      method: "DELETE",
      ...options
    });
  }

  // PATCH request
  protected async patch(
    endpoint: string,
    schema: z.Schema,
    data?: any,
    options: RequestOptions = {}
  ): Promise<z.infer<typeof schema>> {
    return this.makeRequest(endpoint, schema, {
      method: "PATCH",
      data,
      ...options
    });
  }

  // Update configuration
  updateConfig(config: Partial<ApiClientConfig>): void {
    this._config = { ...this._config, ...config };

    // Update axios instance configuration
    if (config.baseURL) {
      this._axiosInstance.defaults.baseURL = config.baseURL;
    }
    if (config.timeout) {
      this._axiosInstance.defaults.timeout = config.timeout;
    }
    if (config.headers) {
      this._axiosInstance.defaults.headers.common = {
        ...this._axiosInstance.defaults.headers.common,
        ...config.headers
      };
    }
  }

  // Get current configuration
  getConfig(): ApiClientConfig {
    return { ...this._config };
  }

  // Get axios instance for advanced usage
  getAxiosInstance(): AxiosInstance {
    return this._axiosInstance;
  }
}
